type DeepFocusT = {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
  focusLabel: string;
  focusValue: string;
  streakLabel: string;
  streakValue: string;
  pillars: [string, string, string][];
};

const defaultT: DeepFocusT = {
  eyebrow: "Section 03 — Productivity & focus",
  title: <>Built for <em className="urgent">deep focus.</em></>,
  body: "Use focus timers, fullscreen work modes, streak tracking, and collaborative environments designed to help people stay productive — together.",
  focusLabel: "Focus minutes · last 7 days",
  focusValue: "604",
  streakLabel: "Streak · sessions",
  streakValue: "12 days",
  pillars: [
    ["01", "Focus timers", "Pomodoro and custom intervals."],
    ["02", "Fullscreen work modes", "Distraction-free deep work."],
    ["03", "Streak tracking", "Daily consistency that compounds."],
    ["04", "Collaborative environments", "Quiet rooms with shared accountability."],
  ],
};

function FocusBars() {
  const bars = [62, 48, 84, 70, 96, 110, 134];
  const labels = ["M", "T", "W", "T", "F", "S", "S"];
  const max = 140;
  const w = 360, h = 140;
  const bw = w / bars.length - 12;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full text-secondary" aria-hidden="true">
      <line x1="0" y1={h - 18} x2={w} y2={h - 18} stroke="currentColor" strokeWidth="0.4" opacity="0.25" />
      {bars.map((v, i) => {
        const bh = (v / max) * (h - 30);
        const x = i * (bw + 12) + 6;
        const y = h - 18 - bh;
        const peak = i === bars.length - 1;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw} height={bh} fill="currentColor" opacity={peak ? 1 : 0.35} />
            <text x={x + bw / 2} y={h - 4} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.6">
              {labels[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function StreakLine() {
  const pts = [20, 25, 22, 30, 35, 33, 42, 48, 55, 60, 72, 80];
  const w = 360, h = 100;
  const step = w / (pts.length - 1);
  const line = pts.map((p, i) => `${i * step},${h - p}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full text-primary" aria-hidden="true">
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.5" />
      {pts.map((p, i) => (
        <circle key={i} cx={i * step} cy={h - p} r={i === pts.length - 1 ? 3 : 1.5} fill="currentColor" />
      ))}
    </svg>
  );
}

export const DeepFocus = ({ t = defaultT }: { t?: DeepFocusT }) => {
  return (
    <section className="py-24 lg:py-32 border-t border-border/40 bg-muted/10" aria-labelledby="deep-focus-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <span className="eyebrow-neon">{t.eyebrow}</span>
          <h2 id="deep-focus-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            {t.title}
          </h2>
          <div className="hairline w-32 mt-8 mb-6" />
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t.body}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <ol className="lg:col-span-5 space-y-6">
            {t.pillars.map(([n, title, body]) => (
              <li key={title} className="border-t border-border pt-4">
                <div className="eyebrow mb-2">{n}</div>
                <h3 className="font-display text-xl text-foreground mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground leading-snug">{body}</p>
              </li>
            ))}
          </ol>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <div className="border border-border bg-card/40 p-5">
              <div className="flex items-baseline justify-between mb-1">
                <span className="eyebrow">{t.focusLabel}</span>
              </div>
              <div className="font-display text-4xl text-foreground tabular-nums mb-3">{t.focusValue}</div>
              <div className="aspect-[16/9]"><FocusBars /></div>
            </div>
            <div className="border border-border bg-card/40 p-5">
              <div className="flex items-baseline justify-between mb-1">
                <span className="eyebrow">{t.streakLabel}</span>
              </div>
              <div className="font-display text-4xl text-foreground tabular-nums mb-3">{t.streakValue}</div>
              <div className="aspect-[16/9]"><StreakLine /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
